import {
  Box,
  HStack,
  Button,
  Text as ChakraText,
  VStack,
} from '@chakra-ui/react'
import { Form, Formik } from 'formik'
import React, { useContext } from 'react'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../ui-kit'
import { allowedBusinessCountries } from '../../../utils/countries'
import { usStatesCodeMap } from '../../../utils/USStates'
import type { BillingDetails } from '../../interfaces'
import { UserContext } from '../../providers'
import { MainContentHeadingBox } from '../Base/MainContent'
import PermissionGuard from "../../components/PermissionGuard"
import { SelectField, TextField } from '../../components/Form'
import useToast from '../../hooks/useToast'
import { PermissionsEnum } from '../../../utils'
import { DiscountedPricingAlertBox } from '../../components/Billing/DiscountedPricing'

const BillingDetailsLayout = ({
  valuesFromAdminPanel
}: {
  valuesFromAdminPanel?: BillingDetails
}): React.ReactElement => {
  const { t } = useTranslation()
  const { user } = useContext(UserContext)
  const toast = useToast()

  const { request, loading } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const onSubmit = async (values: BillingDetails): Promise<void> => {
    await request.post('/api/billing/details', values)
    if (request.error) {
      toast({
        description: t('something_went_wrong', 'Something went wrong'),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      return
    }
    toast({
      description: t(
        'success_billing_details',
        'Successfully updated billing details',
      ),
      status: 'success',
      isClosable: true,
      duration: 5000,
      position: 'top-right',
      variant: 'outline',
    })
  }

  return (
    
    <Formik<BillingDetails>
      initialValues={valuesFromAdminPanel ?? {
        clientNumber: user?.clientNumber ?? '',
        company_name: user?.billingDetails.company_name ?? '',
        billing_email: user?.billingDetails.contact_persons?.[0]?.email ?? '',
        address: user?.billingDetails.billing_address?.address ?? '',
        city: user?.billingDetails.billing_address?.city ?? '',
        state: user?.billingDetails.billing_address?.state ?? '',
        country: user?.billingDetails.billing_address?.country ?? '',
        zip: user?.billingDetails.billing_address?.zip ?? '',
        gstin: user?.billingDetails.gstin ?? '',
      }}
      validationSchema={Yup.object().shape({
        clientNumber: Yup.string().required(t('required', 'Required')),
        company_name: Yup.string().required(t('required', 'Required')),
        billing_email: Yup.string().required(t('required', 'Required')),
        address: Yup.string().required(t('required', 'Required')),
        city: Yup.string().required(t('required', 'Required')),
        state: Yup.string().required(t('required', 'Required')),
        country: Yup.string().required(t('required', 'Required')),
        zip: Yup.string().required(t('required', 'Required')),
      })}
      onSubmit={onSubmit}
    >
      {({ values, isSubmitting, dirty, resetForm, isValid }) => (
        <Form>
          <PermissionGuard
            permission={PermissionsEnum.SELF_BILLING_MANAGE}
          >
            {
              (hasPermission: boolean) => (
                <>
                  <MainContentHeadingBox reverse={true}>
                    {
                      hasPermission ? (
                        <HStack spacing="1em">
                          <Button
                            onClick={() => {
                              if (user?.clientNumber) {
                                resetForm({
                                  values: valuesFromAdminPanel ?? {
                                    clientNumber: user.clientNumber,
                                    company_name: user.billingDetails.company_name ?? '',
                                    billing_email:
                                      user.billingDetails.contact_persons?.[0]?.email ?? '',
                                    address:
                                      user.billingDetails.billing_address?.address ?? '',
                                    city: user.billingDetails.billing_address?.city ?? '',
                                    state: user.billingDetails.billing_address?.state ?? '',
                                    country:
                                      user.billingDetails.billing_address?.country ?? '',
                                    zip: user.billingDetails.billing_address?.zip ?? '',
                                    gstin: user.billingDetails.gstin ?? '',
                                  },
                                })
                              }
                            }}
                            variant="outline"
                            isDisabled={!dirty}
                          >
                            {t('cancel')}
                          </Button>
                          <Button
                            isLoading={isSubmitting}
                            isDisabled={!dirty}
                            type="submit"
                          >
                            {t('save')}
                          </Button>
                        </HStack>
                      ) : null
                    }
                  </MainContentHeadingBox>
                  <Box padding="1.5em">
                    <VStack
                      px={8}
                      py={6}
                      maxWidth="750px"
                      justifyContent="flex-start"
                      alignItems="flex-start"
                      spacing="2em"
                    >
                      <DiscountedPricingAlertBox />
                      <ChakraText marginBottom="-1em" color={colors.appSlate}>
                        <b>{t('billing_details', 'Billing details')}</b>
                        <br />
                        {t(
                          'billing_details_description',
                          'Invoice will be created with the following details',
                        )}
                      </ChakraText>
                      <HStack spacing="2em" width="100%" alignItems="flex-start">
                        <TextField<BillingDetails>
                          name="company_name"
                          isDisabled={!hasPermission}
                          label={t('company_name', 'Company Name')}
                          required={true}
                          placeholder="XYZ Pvt Ltd"
                        />

                        <TextField<BillingDetails>
                          name="billing_email"
                          isDisabled={!hasPermission}
                          label={t('billing_email', 'Billing Email')}
                          required={true}
                          placeholder="name@email.com"
                        />
                      </HStack>
                      <HStack width="100%">
                        <SelectField<BillingDetails>
                          name="country"
                          isDisabled={!hasPermission}
                          label={t('country', 'Country')}
                          options={allowedBusinessCountries.map(x => ({
                            label: x,
                            value: x,
                          }))}
                          required={true}
                        />
                      </HStack>
                      <HStack spacing="2em" width="100%" alignItems="flex-start">
                        <TextField<BillingDetails>
                          name="address"
                          isDisabled={!hasPermission}
                          label={t('address', 'Address')}
                          required={true}
                        />
                      </HStack>
                      <HStack spacing="2em" width="100%" alignItems="flex-start">
                        <TextField<BillingDetails>
                          name="city"
                          isDisabled={!hasPermission}
                          label={t('city', 'City')}
                          required={true}
                        />
                        {values.country === 'United States' ? (
                          <SelectField<BillingDetails>
                            name="state"
                            isDisabled={!hasPermission}
                            label={t('state', 'State')}
                            required={true}
                            options={Object.entries(usStatesCodeMap).map(
                              ([value, label]) => ({ value, label }),
                            )}
                          />
                        ) : (
                          <TextField<BillingDetails>
                            name="state"
                            isDisabled={!hasPermission}
                            label={t('state', 'State')}
                            required={true}
                          />
                        )}
                      </HStack>
                      <HStack spacing="2em" width="100%" alignItems="flex-start">
                        <TextField<BillingDetails>
                          name="zip"
                          isDisabled={!hasPermission}
                          label={t('zip_code', 'Zip Code')}
                          required={true}
                        />
                        <TextField<BillingDetails>
                          name="gstin"
                          isDisabled={!hasPermission}
                          label={t('gst_vat_number', 'GST/VAT Number')}
                        />
                      </HStack>
                    </VStack>
                  </Box>
                </>
              )
            }
          </PermissionGuard>
        </Form>
      )}
    </Formik>
  )
}

export default BillingDetailsLayout
